import { Button, InputNumber, Select, Space, Tag } from "antd";
import { Dayjs } from "dayjs";
import { ArrowDownIcon, ArrowUpIcon, GripVerticalIcon, MinusIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePortalCitiesQuery } from "../actions/itinerary.actions";
import PortalFlowSteps from "./PortalFlowSteps";
import { PortalPageHeader } from "./PortalProduct";

export type PortalRouteStop = {
  country: string;
  city: string;
  nights: number;
};

type RouteStopRowProps = {
  stop: PortalRouteStop;
  index: number;
  totalStops: number;
  countries: string[];
  dateLabel: string;
  onChange: (updates: Partial<PortalRouteStop>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
};

const RouteStopRow = ({
  stop,
  index,
  totalStops,
  countries,
  dateLabel,
  onChange,
  onMove,
  onRemove,
}: RouteStopRowProps) => {
  const [citySearch, setCitySearch] = useState("");
  const citiesQuery = usePortalCitiesQuery(stop.country, citySearch);

  return (
    <div className="grid grid-cols-[28px_minmax(0,1fr)_auto] gap-2 px-3 py-3 md:grid-cols-[36px_130px_minmax(180px,1fr)_120px_150px_92px] md:items-center">
      <GripVerticalIcon className="mt-2 h-4 w-4 text-gray-300 md:mt-0" />
      <div className="col-span-2 md:col-span-1">
        <Select
          value={stop.country}
          className="w-full"
          options={countries.map((country) => ({ value: country }))}
          onChange={(country) => onChange({ country, city: "" })}
        />
      </div>
      <div className="col-start-2 min-w-0 md:col-auto">
        <Select
          showSearch
          value={stop.city || undefined}
          searchValue={citySearch}
          loading={citiesQuery.isFetching}
          disabled={!stop.country}
          placeholder="Select city"
          className="w-full"
          filterOption={false}
          options={(citiesQuery.data || []).map((city) => ({ label: city, value: city }))}
          onSearch={setCitySearch}
          onChange={(city) => {
            onChange({ city });
            setCitySearch("");
          }}
        />
      </div>
      <div className="col-start-3 row-start-2 md:col-auto md:row-auto">
        <Space.Compact className="w-full">
          <Button
            icon={<MinusIcon className="h-3.5 w-3.5" />}
            aria-label={`Decrease nights for stop ${index + 1}`}
            disabled={stop.nights <= 1}
            onClick={() => onChange({ nights: Math.max(1, Number(stop.nights || 1) - 1) })}
          />
          <InputNumber
            value={stop.nights}
            min={1}
            max={30}
            precision={0}
            controls={false}
            aria-label={`Nights for stop ${index + 1}`}
            className="!w-14 [&_input]:!text-center"
            onChange={(value) => onChange({ nights: Number(value || 1) })}
          />
          <Button
            icon={<PlusIcon className="h-3.5 w-3.5" />}
            aria-label={`Increase nights for stop ${index + 1}`}
            disabled={stop.nights >= 30}
            onClick={() => onChange({ nights: Math.min(30, Number(stop.nights || 1) + 1) })}
          />
        </Space.Compact>
      </div>
      <div className="col-start-2 text-xs font-medium tabular-nums text-[#344054] md:col-auto">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 md:hidden">
          Dates
        </span>
        {dateLabel}
      </div>
      <div className="col-start-3 flex justify-end md:col-auto">
        <Button
          type="text"
          size="small"
          disabled={index === 0}
          icon={<ArrowUpIcon className="h-3.5 w-3.5" />}
          aria-label={`Move stop ${index + 1} earlier`}
          onClick={() => onMove(-1)}
        />
        <Button
          type="text"
          size="small"
          disabled={index === totalStops - 1}
          icon={<ArrowDownIcon className="h-3.5 w-3.5" />}
          aria-label={`Move stop ${index + 1} later`}
          onClick={() => onMove(1)}
        />
        <Button
          type="text"
          size="small"
          danger
          icon={<Trash2Icon className="h-3.5 w-3.5" />}
          aria-label={`Remove stop ${index + 1}`}
          onClick={onRemove}
        />
      </div>
    </div>
  );
};

/** Full-page step that configures an ordered city route and derives the trip duration from its stops. */
const PortalRoutePlannerPage = ({
  open,
  countries,
  startDate,
  totalNights,
  value,
  onCancel,
  onSave,
  onUseCountriesOnly,
}: {
  open: boolean;
  countries: string[];
  startDate: Dayjs;
  totalNights: number;
  value: PortalRouteStop[];
  onCancel: () => void;
  onSave: (routeStops: PortalRouteStop[], allocatedNights: number) => void;
  onUseCountriesOnly: () => void;
}) => {
  const [draftStops, setDraftStops] = useState<PortalRouteStop[]>(value);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setDraftStops(value);
  }, [open, value]);

  useEffect(() => {
    if (open) pageRef.current?.scrollIntoView({ block: "start" });
  }, [open]);

  const computedStops = useMemo(() => {
    let elapsedNights = 0;
    return draftStops.map((stop) => {
      const stopStart = startDate.add(elapsedNights, "day");
      elapsedNights += Number(stop.nights || 0);
      return {
        ...stop,
        startDate: stopStart,
        endDate: startDate.add(elapsedNights, "day"),
      };
    });
  }, [draftStops, startDate]);
  const allocatedNights = draftStops.reduce((sum, stop) => sum + Number(stop.nights || 0), 0);
  const usedCountries = new Set(draftStops.map((stop) => stop.country.trim().toLocaleLowerCase()).filter(Boolean));
  const missingCountries = countries.filter(
    (country, index) =>
      countries.findIndex(
        (candidate) => candidate.trim().toLocaleLowerCase() === country.trim().toLocaleLowerCase(),
      ) === index && !usedCountries.has(country.trim().toLocaleLowerCase()),
  );
  const stopsMissingCities = draftStops.some((stop) => !stop.country.trim() || !stop.city.trim());
  const hasInvalidNights = draftStops.some((stop) => !Number.isInteger(Number(stop.nights)) || Number(stop.nights) < 1);
  const exceedsNightLimit = allocatedNights > 30;
  const canAddStop = draftStops.length < 30 && allocatedNights < 30;
  const canSave =
    draftStops.length > 0 &&
    missingCountries.length === 0 &&
    !stopsMissingCities &&
    !hasInvalidNights &&
    allocatedNights > 0 &&
    !exceedsNightLimit;

  const updateStop = (index: number, updates: Partial<PortalRouteStop>) => {
    setDraftStops((current) =>
      current.map((stop, stopIndex) => (stopIndex === index ? { ...stop, ...updates } : stop)),
    );
  };

  const moveStop = (index: number, direction: -1 | 1) => {
    setDraftStops((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const addStop = () => {
    if (!canAddStop) return;
    setDraftStops((current) => {
      const assigned = current.reduce((sum, stop) => sum + Number(stop.nights || 0), 0);
      const remaining = totalNights - assigned;
      const next = [...current];
      let newStopNights = Math.max(1, remaining);
      if (remaining < 1) {
        // Keep the trip length stable by borrowing a night from the longest stop.
        const donorIndex = next.reduce(
          (best, stop, index) => (Number(stop.nights) > Number(next[best].nights) ? index : best),
          0,
        );
        if (next.length && Number(next[donorIndex].nights) > 1) {
          next[donorIndex] = { ...next[donorIndex], nights: Number(next[donorIndex].nights) - 1 };
        }
        newStopNights = 1;
      }
      next.push({ country: countries[0] || "", city: "", nights: newStopNights });
      return next;
    });
  };

  if (!open) return null;

  return (
    <div ref={pageRef} className="space-y-4 scroll-mt-4">
      <div className="space-y-3">
        <PortalFlowSteps current={2} />
        <PortalPageHeader
          title="Plan route stops"
          description="Assign nights across each city. The trip duration will follow the route total."
        />
      </div>
      <section
        className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
        aria-label="Route stops"
      >
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-[#FBFBFB] px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400">
              Destination countries from trip details
            </p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {countries.map((country) => (
                <Tag
                  key={country}
                  className="!m-0 !rounded-md !border-(--brand-primary)/35 !bg-(--brand-primary)/10 !font-semibold !text-(--brand-primary)"
                >
                  {country}
                </Tag>
              ))}
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p
              className={`text-sm font-semibold tabular-nums ${allocatedNights === totalNights ? "text-emerald-700" : "text-amber-700"}`}
            >
              {allocatedNights} / {totalNights} nights assigned
            </p>
            {computedStops.length ? (
              <p className="mt-0.5 text-[11px] text-gray-500">
                {computedStops[0].startDate.format("DD MMM YYYY")}–
                {computedStops[computedStops.length - 1].endDate.format("DD MMM YYYY")}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
          <div className="hidden grid-cols-[36px_130px_minmax(180px,1fr)_120px_150px_92px] bg-[#F7F5F1] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 md:grid">
            <span />
            <span>Destination</span>
            <span>City stop</span>
            <span>Nights</span>
            <span>Dates</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="divide-y divide-gray-100">
            {computedStops.map((stop, index) => (
              <RouteStopRow
                key={`${index}-${stop.country}-${stop.city}`}
                stop={stop}
                index={index}
                totalStops={computedStops.length}
                countries={countries}
                dateLabel={`${stop.startDate.format("DD MMM")}–${stop.endDate.format("DD MMM")}`}
                onChange={(updates) => updateStop(index, updates)}
                onMove={(direction) => moveStop(index, direction)}
                onRemove={() => setDraftStops((current) => current.filter((_stop, stopIndex) => stopIndex !== index))}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={addStop}
            disabled={!canAddStop}
            className="flex w-full items-center justify-center gap-2 border-t border-dashed border-gray-300 bg-[#FBFBFB] px-3 py-3 text-xs font-semibold text-(--brand-primary) transition-colors hover:bg-[var(--brand-primary-soft)] disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-[#FBFBFB]"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add a stop
          </button>
        </div>

        {draftStops.length === 0 ? (
          <p className="mt-3 text-xs font-medium text-amber-700">Add a city stop and allocate the itinerary nights.</p>
        ) : missingCountries.length > 0 ? (
          <p className="mt-3 text-xs font-medium text-red-600">Add a city stop for {missingCountries.join(", ")}.</p>
        ) : stopsMissingCities ? (
          <p className="mt-3 text-xs font-medium text-red-600">Select a destination and city for every route stop.</p>
        ) : hasInvalidNights ? (
          <p className="mt-3 text-xs font-medium text-red-600">Every route stop must have at least one whole night.</p>
        ) : exceedsNightLimit ? (
          <p className="mt-3 text-xs font-medium text-red-600">A trip can contain at most 30 nights.</p>
        ) : allocatedNights !== totalNights ? (
          <p className="mt-3 text-xs font-medium text-amber-700">
            Setting this route will update the trip duration from {totalNights} to {allocatedNights} night
            {allocatedNights === 1 ? "" : "s"}.
          </p>
        ) : null}
      </section>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button type="text" danger onClick={onUseCountriesOnly}>
          Reset
        </Button>
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            disabled={!canSave}
            className="!bg-(--brand-secondary)"
            onClick={() => onSave(draftStops, allocatedNights)}
          >
            {allocatedNights !== totalNights ? "Set route and update nights" : "Set route"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalRoutePlannerPage;
